import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchHandbuchLage, fetchPlanfassungen } from '../../services/handbuchService';
import type { Planfassung } from '../../services/handbuchService';
import { fetchKoordinierungsLage } from '../../services/koordinierungService';
import { fetchProjektDokumente } from '../../services/projektDokumenteService';
import { ladePlanQuelle } from '../sige-einweisung/useEinweisungen';
import { gewerkeAusPlan } from './handbuchLogic';
import type { KoordinierungPaar } from '../koordinierung/koordinierungLogic';
import type { ProjektDokument } from '../dokumente/types';
import type {
    Arbeitsschritt, Gefaehrdung, NotrufplanInhalt, Schutzmassnahme, SigeProject,
} from '../sige-plan/types';
import type { GewerkAusPlan, Handbuch, HandbuchGewerk, HandbuchNachweis } from './types';

/**
 * Alles, was die Kapitelseiten ausser der Handbuchlage selbst brauchen.
 *
 * Bewusst EIN Zustand und nicht acht: die neun Kapitel werden gemeinsam
 * gezeichnet, und ein halb geladener Satz zeigte auf Blatt 5 einen Stand, den
 * Blatt 6 noch nicht kennt.
 */
export interface KapitelQuelle {
    sigeProject: SigeProject | null;
    arbeitsschritte: Arbeitsschritt[];
    gefaehrdungen: Gefaehrdung[];
    massnahmen: Schutzmassnahme[];
    /** Inhalt des Blocks `sige_bloecke[typ='notrufplan']`. Wird NUR gelesen —
     *  es gibt genau einen Notrufplan je Projekt, und der gehoert dem
     *  SiGe-Plan. */
    notrufplan: NotrufplanInhalt | null;
    paare: KoordinierungPaar[];
    /** `true`, wenn Migration Nr. 40 noch nicht gefahren ist. Der Unterschied
     *  zaehlt: „noch nichts bewertet" und „die Tabelle gibt es nicht" sehen
     *  gleich aus, meinen aber Gegenteiliges — und eine leere
     *  Gefaehrdungsmatrix liest sich wie eine Entwarnung. */
    paareTabelleFehlt: boolean;
    dokumente: ProjektDokument[];
    planFassungen: Planfassung[];
}

const LEERE_KAPITELQUELLE: KapitelQuelle = {
    sigeProject: null,
    arbeitsschritte: [],
    gefaehrdungen: [],
    massnahmen: [],
    notrufplan: null,
    paare: [],
    paareTabelleFehlt: false,
    dokumente: [],
    planFassungen: [],
};

/**
 * Laedt die Lage des SiGe-Handbuchs eines Projekts.
 *
 * Legt bewusst NICHTS an: das blosse Oeffnen der Kachel darf keinen Datensatz
 * erzeugen. Dieselbe Ueberlegung wie beim Schreibschutz fuer
 * Nur-Ansicht-Projekte, wo Selbstheilungs-Migrationen beim reinen Lesen
 * geschrieben hatten. Angelegt wird erst, wenn jemand „Handbuch anlegen"
 * drueckt.
 *
 * Der Hook haelt Zustand und nichts weiter. Jede Regel, die man pruefen kann,
 * steht daneben in `handbuchLogic.ts` — stehende Entscheidung „duenner
 * Container, Logik in Hooks und puren Funktionen"; es gibt kein jsdom, also
 * ist nur geprueft, was ausserhalb einer Komponente liegt.
 */
export const useHandbuch = (projectId: string | null) => {
    const [handbuch, setHandbuch] = useState<Handbuch | null>(null);
    const [gewerke, setGewerke] = useState<HandbuchGewerk[]>([]);
    const [nachweise, setNachweise] = useState<HandbuchNachweis[]>([]);
    const [laden, setLaden] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    /**
     * Die Gewerke der neuesten Planfassung.
     *
     * Getrennt vom Rest und NICHT beim Oeffnen geladen: das sind fuenf
     * Abfragen (`ladePlanQuelle`), die niemanden interessieren, der nur das
     * fertige Handbuch ansehen oder drucken will. Geholt wird auf Knopfdruck
     * und beim Wechsel auf den Gewerke-Reiter.
     */
    const [planGewerke, setPlanGewerke] = useState<GewerkAusPlan[]>([]);
    const [planGeladen, setPlanGeladen] = useState(false);
    const [planLaeuft, setPlanLaeuft] = useState(false);

    // Lauf-Zaehler gegen spaete Antworten: wechselt der Anwender das Projekt,
    // waehrend die erste Abfrage noch laeuft, darf deren Ergebnis die
    // inzwischen geladene zweite Lage nicht ueberschreiben.
    /**
     * Die Quellen der Kapitelseiten.
     *
     * Getrennt vom Rest und NICHT beim Oeffnen geladen — genau wie
     * `planGewerke`: das sind acht Abfragen, die niemanden interessieren, der
     * nur den Bearbeitungsstand sehen oder eine Gewerkeseite anlegen will.
     * Geholt wird beim Wechsel auf den Kapitel-Reiter.
     */
    const [kapitelQuelle, setKapitelQuelle] = useState<KapitelQuelle>(LEERE_KAPITELQUELLE);
    const [kapitelGeladen, setKapitelGeladen] = useState(false);
    const [kapitelLaeuft, setKapitelLaeuft] = useState(false);

    const lauf = useRef(0);
    const planLauf = useRef(0);
    const kapitelLauf = useRef(0);

    const neuLaden = useCallback(async () => {
        const meiner = ++lauf.current;
        if (!projectId || projectId === 'temp-id') {
            setHandbuch(null); setGewerke([]); setNachweise([]);
            setLaden(false); setFehler(null);
            return;
        }
        setLaden(true);
        setFehler(null);
        try {
            const lage = await fetchHandbuchLage(projectId);
            if (meiner !== lauf.current) return;
            setHandbuch(lage.handbuch);
            setGewerke(lage.gewerke);
            setNachweise(lage.nachweise);
        } catch (e) {
            if (meiner !== lauf.current) return;
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            if (meiner === lauf.current) setLaden(false);
        }
    }, [projectId]);

    useEffect(() => { void neuLaden(); }, [neuLaden]);

    // Projektwechsel setzt auch den Planstand zurueck — sonst zeigte das neue
    // Projekt die Gewerke des alten.
    useEffect(() => {
        setPlanGewerke([]);
        setPlanGeladen(false);
        setKapitelQuelle(LEERE_KAPITELQUELLE);
        setKapitelGeladen(false);
    }, [projectId]);

    /**
     * Holt die Gewerke aus der neuesten, nicht geloeschten Planfassung.
     *
     * `ladePlanQuelle` ist derselbe Weg, den die Einweisung geht (inklusive
     * `.is('deleted_at', null)`) — bewusst wiederverwendet und nicht
     * nachgebaut: eine zweite Abfrage mit anderen Filtern haette irgendwann
     * eine andere Planfassung geliefert als die Einweisung, und dann stritten
     * zwei Module darueber, was im Plan steht.
     *
     * Ein Fehler hier wird gemeldet, aber die geladene Lage bleibt stehen: das
     * Handbuch ist auch ohne Plan les- und druckbar.
     */
    const ladePlanGewerke = useCallback(async (): Promise<GewerkAusPlan[]> => {
        const meiner = ++planLauf.current;
        if (!projectId || projectId === 'temp-id') return [];
        setPlanLaeuft(true);
        try {
            const quelle = await ladePlanQuelle(projectId);
            const liste = gewerkeAusPlan(quelle.arbeitsschritte || []);
            if (meiner !== planLauf.current) return liste;
            setPlanGewerke(liste);
            setPlanGeladen(true);
            return liste;
        } finally {
            if (meiner === planLauf.current) setPlanLaeuft(false);
        }
    }, [projectId]);

    /**
     * Holt alles, was die Kapitelseiten zeigen.
     *
     * ═══ WARUM DIE PLANQUELLE ZWEIMAL GELESEN WIRD ═════════════════════════
     * `ladePlanQuelle` liefert Arbeitsschritte, Gefaehrdungen, Massnahmen und
     * Bloecke; `fetchKoordinierungsLage` liefert die Bewertungen — und mit
     * ihnen `tabelleFehlt`. Die schmalere `fetchKoordinierungPaare` gibt es,
     * aber sie schluckt genau diesen Unterschied: sie liefert eine leere Liste,
     * gleichgueltig ob nichts bewertet wurde oder die Migration fehlt. Eine
     * leere Gefaehrdungsmatrix liest sich wie eine Entwarnung, deshalb sind
     * uns die zwei zusaetzlichen Abfragen den ehrlichen Hinweis wert.
     *
     * Fehler EINES Zweigs kippen nicht den ganzen Reiter: das Handbuch ist auch
     * ohne Plan, ohne Ablage und ohne Koordinierung les- und druckbar.
     */
    const ladeKapitelQuelle = useCallback(async (): Promise<KapitelQuelle> => {
        const meiner = ++kapitelLauf.current;
        if (!projectId || projectId === 'temp-id') return LEERE_KAPITELQUELLE;
        setKapitelLaeuft(true);
        try {
            const [plan, koordinierung, dokumente, fassungen] = await Promise.all([
                ladePlanQuelle(projectId).catch(() => null),
                fetchKoordinierungsLage(projectId).catch(() => null),
                fetchProjektDokumente(projectId).catch(() => [] as ProjektDokument[]),
                fetchPlanfassungen(projectId).catch(() => [] as Planfassung[]),
            ]);
            const notrufBlock = (plan?.bloecke || []).find(b => b.typ === 'notrufplan');
            // Wie `ladePlanGewerke`: die frisch geholte Lage wird ZURUECKGEGEBEN und
            // nicht nur in den Zustand gelegt. Aufrufer, die sie sofort brauchen
            // (`kapitelGeladen ? kapitelQuelle : await ladeKapitelQuelle()`), bekamen
            // sonst `undefined` — der naechste Zugriff darauf brach ab.
            const neueQuelle: KapitelQuelle = {
                sigeProject: plan?.sigeProject ?? null,
                arbeitsschritte: plan?.arbeitsschritte || [],
                gefaehrdungen: plan?.gefaehrdungen || [],
                massnahmen: plan?.massnahmen || [],
                notrufplan: (notrufBlock?.inhalt as NotrufplanInhalt) ?? null,
                paare: koordinierung?.paare || [],
                paareTabelleFehlt: koordinierung?.tabelleFehlt ?? false,
                dokumente,
                planFassungen: fassungen,
            };
            if (meiner !== kapitelLauf.current) return neueQuelle;

            setKapitelQuelle(neueQuelle);
            setKapitelGeladen(true);

            // Die Gewerke des Plans fallen hier mit ab — sonst zeigte der
            // Kapitel-Reiter Zeitraeume aus dem Plan erst, nachdem jemand
            // vorher den Gewerke-Reiter geoeffnet hat.
            if (plan && !planGeladen) {
                setPlanGewerke(gewerkeAusPlan(plan.arbeitsschritte || []));
                setPlanGeladen(true);
            }
            return neueQuelle;
        } finally {
            if (meiner === kapitelLauf.current) setKapitelLaeuft(false);
        }
    }, [projectId, planGeladen]);

    /** Eine geaenderte Gewerkeseite zurueckspielen, ohne alles neu zu laden —
     *  sonst springt die Liste bei jedem Tastendruck. */
    const uebernehmeGewerk = useCallback((gewerk: HandbuchGewerk) => {
        setGewerke(alt => (alt.some(g => g.id === gewerk.id)
            ? alt.map(g => (g.id === gewerk.id ? gewerk : g))
            : [...alt, gewerk].sort((a, b) => a.sortierung - b.sortierung)));
    }, []);

    const ergaenzeGewerke = useCallback((neue: HandbuchGewerk[]) => {
        if (!neue?.length) return;
        setGewerke(alt => {
            const bekannt = new Set(alt.map(g => g.id));
            return [...alt, ...neue.filter(g => !bekannt.has(g.id))]
                .sort((a, b) => a.sortierung - b.sortierung);
        });
    }, []);

    /** Entfernt eine Gewerkeseite MIT ihren Nachweisen — die Datenbank
     *  raeumt sie ueber `on delete cascade` weg, der Bildschirm muss
     *  mitziehen, sonst stehen dort Nachweise ohne Seite. */
    const entferneGewerk = useCallback((id: string) => {
        setGewerke(alt => alt.filter(g => g.id !== id));
        setNachweise(alt => alt.filter(n => n.handbuch_gewerk_id !== id));
    }, []);

    const uebernehmeNachweis = useCallback((nachweis: HandbuchNachweis) => {
        setNachweise(alt => (alt.some(n => n.id === nachweis.id)
            ? alt.map(n => (n.id === nachweis.id ? nachweis : n))
            : [...alt, nachweis]));
    }, []);

    const ergaenzeNachweise = useCallback((neue: HandbuchNachweis[]) => {
        if (!neue?.length) return;
        setNachweise(alt => {
            const bekannt = new Set(alt.map(n => n.id));
            return [...alt, ...neue.filter(n => !bekannt.has(n.id))];
        });
    }, []);

    const entferneNachweis = useCallback((id: string) => {
        setNachweise(alt => alt.filter(n => n.id !== id));
    }, []);

    return {
        handbuch, setHandbuch,
        gewerke, setGewerke,
        nachweise, setNachweise,
        laden, fehler, neuLaden,
        planGewerke, planGeladen, planLaeuft, ladePlanGewerke,
        kapitelQuelle, kapitelGeladen, kapitelLaeuft, ladeKapitelQuelle,
        uebernehmeGewerk, ergaenzeGewerke, entferneGewerk,
        uebernehmeNachweis, ergaenzeNachweise, entferneNachweis,
    };
};
